import dayjs from "dayjs";

import type * as T from "@src/types";
import { calculatePositionSummary } from "@src/utils/formatters";
import { getPositionNetProfit, getPositionProfitPct } from "@src/utils/positionProfit";
import {
  TRACKING_ENDED_EXPLANATION,
  TRACKING_ENDED_LABEL,
  isPositionTrackingEnded,
} from "@src/utils/positionStatus";

// https://www.tradingview.com/charting-library-docs/latest/customization/overrides/Shapes-and-Overrides/

const LONG_COLOR = "#4c8dff";
const SHORT_COLOR = "#fba904";
const WIN_COLOR = "#7afb5d";
const LOSS_COLOR = "#ffafaf";

const utcOff = false;

// Exit reason icons shown as small markers on the chart next to exit labels
// Checked against both reason_key and reason_note (reason_note allows sub-typing generic keys)
const EXIT_REASON_ICONS: Record<string, string> = {
  time_limit: "\uD83D\uDD5A", // 🕚 clock
  volatility_sma: "\uD83D\uDD5A", // 🕚 clock — SMA divergence force-close (stored in reason_note)
  trend_slope: "\u{1F4C8}", // 📈 chart — RangePrint trend-slope force-close (stored in reason_note)
};

/**
 * Get the exit icon key from a position's close movement.
 * Checks reason_key first, then reason_note for sub-typed exits (e.g. strategy_exit + volatility_sma note).
 */
const getExitReasonKey = (pos: T.AppPosition): string | null => {
  if (!pos.movements?.length) return null;
  const closeMovement = pos.movements.find((m) => m.action_id === 4);
  if (!closeMovement) return null;
  const key = closeMovement.reason_key;
  if (key && EXIT_REASON_ICONS[key]) return key;
  // Fall back to reason_note for sub-typed exits (e.g. volatility_sma under strategy_exit)
  const note = closeMovement.reason_note;
  if (note && EXIT_REASON_ICONS[note]) return note;
  return key ?? null;
};

export const getTickLevel = (tickSize: number, pos: T.AppPosition) => {
  if (pos.stop_loss) return pos.stop_loss;
  if (!pos.exit_price) return 0;

  return Math.abs(Math.trunc((pos.exit_price - pos.entry_price) / tickSize));
};

export const longShort = (pos: T.AppPosition, isWin: boolean) => {
  // TODO: do the necessary adjustments to the profitLevel and stopLevel;
  return {
    shape: `${pos.direction_name.toLowerCase()}_position` as "long_position" | "short_position",
    overrides: {
      profitLevel: 1, // getTickLevel(50, pos),
      stopLevel: 1, // getTickLevel(50, pos),
      risk: pos.leverage ? pos.leverage : 1,
      accountSize: pos.entry_amount,
      stopBackground: isWin ? "#FBA1A1" : LOSS_COLOR,
      profitBackground: isWin ? WIN_COLOR : "#93D593",
      drawBorder: false,
    },
  };
};

interface MultipointShapeParams {
  points: T.TV.ShapePoint[];
  options: T.TV.CreateMultipointShapeOptions<T.TV.Overrides>;
}

export const getSimplePositionArrow = (
  pos: T.AppPosition,
  lastCandleTime?: number,
): MultipointShapeParams => {
  // make the arrow to be a dotted one and just in horizontal direction for open positions
  const posStillOpen = pos.end_ts === null;

  const startUnix = dayjs(pos.start_ts).utc(utcOff).unix();
  const endUnix = posStillOpen
    ? lastCandleTime || dayjs().utc(utcOff).unix()
    : dayjs(pos.end_ts).utc(utcOff).unix();

  const shapePoints = [
    {
      time: startUnix,
      price: pos.entry_price,
    },
    {
      time: endUnix,
      price: posStillOpen ? pos.entry_price : pos.exit_price,
    },
  ];

  // https://www.tradingview.com/charting-library-docs/latest/api/interfaces/Charting_Library.ArrowLineToolOverrides
  const shapeOptions: T.TV.CreateMultipointShapeOptions<T.TV.Overrides> = {
    shape: "arrow",
    lock: true,
    overrides: {
      text: posStillOpen ? "[ ? ]" : "",
      fontsize: 12,
      linecolor: pos.direction_name === "long" ? LONG_COLOR : SHORT_COLOR,
      textcolor: "#ffffff",
      linewidth: 2,
      linestyle: posStillOpen ? 1 : 0,
      showDateTimeRange: true,
      showPercentPriceRange: true,
      showLabel: false,
    },
  };

  return {
    points: shapePoints,
    options: shapeOptions,
  };
};

/**
 * Temporary dotted arrow rendered when a position's end is visible but its start
 * is outside the loaded data range. Starts from the visible range's left edge
 * to indicate the position extends further left. Replaced by the real arrow
 * once the chart scrolls far enough to load data at the position's start.
 */
export const getTemporaryPositionArrow = (
  pos: T.AppPosition,
  visibleFrom: number,
  lastCandleTime?: number,
): MultipointShapeParams => {
  const posStillOpen = pos.end_ts === null;
  const startUnix = dayjs(pos.start_ts).utc(utcOff).unix();
  const endUnix = posStillOpen
    ? lastCandleTime || dayjs().utc(utcOff).unix()
    : dayjs(pos.end_ts).utc(utcOff).unix();
  const endPrice = posStillOpen ? pos.entry_price : pos.exit_price;

  // Interpolate the price at visibleFrom along the original arrow's line
  // so the temp arrow has the same angle/slope as the real one
  const t = (visibleFrom - startUnix) / (endUnix - startUnix);
  const interpolatedPrice = pos.entry_price + t * ((endPrice ?? pos.entry_price) - pos.entry_price);

  const shapePoints = [
    {
      time: visibleFrom,
      price: interpolatedPrice,
    },
    {
      time: endUnix,
      price: endPrice,
    },
  ];

  const shapeOptions: T.TV.CreateMultipointShapeOptions<T.TV.Overrides> = {
    shape: "arrow",
    lock: true,
    overrides: {
      linecolor: pos.direction_name === "long" ? LONG_COLOR : SHORT_COLOR,
      linewidth: 2,
      linestyle: posStillOpen ? 1 : 0,
      showDateTimeRange: false,
      showPercentPriceRange: false,
      showLabel: false,
    },
  };

  return { points: shapePoints, options: shapeOptions };
};

export const getPositionExitLabelPointer = (
  pos: T.AppPosition,
  chart: T.TV.IChartWidgetApi,
): void => {
  // Only create labels for closed positions
  if (pos.end_ts === null || isPositionTrackingEnded(pos)) return;

  const posProfit = getPositionNetProfit(pos)!;
  const isWin = posProfit > 0;

  // Format profit text: +12.345 or -5.678 (3 decimals to match position cells)
  // Prepend exit reason icon if applicable (e.g. 🕚 for time_limit)
  const exitReasonKey = getExitReasonKey(pos);
  const exitIcon = exitReasonKey ? EXIT_REASON_ICONS[exitReasonKey] : null;
  const profitText = `${exitIcon ? exitIcon + " " : ""}${isWin ? "+" : ""}${posProfit.toFixed(3)}`;

  // Exit time and price
  const exitTime = dayjs(pos.end_ts).utc(utcOff).unix();
  const exitPrice = pos.exit_price || pos.entry_price;

  // Color based on win/loss - green for wins, red for losses
  const color = isWin ? WIN_COLOR : LOSS_COLOR;

  // For long positions, use twice the arrow height to avoid showing within price range
  const arrowHeight = pos.direction_name === "long" ? 40 : 20;
  const useDeprecatedFn = false;

  if (!useDeprecatedFn) {
    // Guard against invalid prices that would cause NaN/Infinity in calculations
    if (exitPrice <= 0) {
      console.warn(
        "[getPositionExitLabelPointer] Invalid exit price:",
        exitPrice,
        "for position:",
        pos.id,
      );
      return;
    }

    // New API method - using arrow_up shape for text + vertical arrow for visual connection
    // Arrow extends down from exit price
    //
    // Use logarithmic scaling for consistent visual appearance across all price levels:
    // - BTC at $100,000 → magnitude 5 → baseOffset 100 → offset ~$600 (long) / ~$400 (short)
    // - ETH at $3,000 → magnitude 3 → baseOffset 1 → offset ~$6 (long) / ~$4 (short)
    // - Small cap at $0.05 → magnitude -2 → baseOffset 0.00001 → offset ~$0.00006 (long)
    const magnitude = Math.floor(Math.log10(exitPrice));
    const baseOffset = Math.pow(10, magnitude - 3); // 0.1% of order of magnitude
    const multiplier = arrowHeight === 40 ? 6 : 4; // Longs need more offset
    const arrowOffsetPrice = baseOffset * multiplier;
    const arrowBottomPrice = exitPrice - arrowOffsetPrice;

    // Text positioned slightly above arrow tip to overlap and eliminate gap
    const textOverlap = baseOffset * 0.5;
    const textPrice = arrowBottomPrice + textOverlap;

    // Draw text label
    chart.createShape(
      { time: exitTime, price: textPrice },
      {
        shape: "arrow_up",
        lock: true,
        disableSelection: true,
        disableSave: true,
        disableUndo: true,
        overrides: {
          text: profitText,
          fontsize: 14,
          bold: true,
          color: color,
          backgroundColor: "transparent",
          backgroundTransparency: 100,
          textColor: color,
          arrowColor: "transparent", // Hide the arrow itself
          transparency: 100, // Make arrow invisible
          borderColor: "transparent",
        },
      },
    );

    // Draw connecting arrow pointing from bottom up to exit price (dimmed)
    chart.createMultipointShape(
      [
        { time: exitTime, price: arrowBottomPrice }, // Start from arrow bottom
        { time: exitTime, price: exitPrice }, // Point to exit price (top)
      ],
      {
        shape: "arrow",
        lock: true,
        disableSelection: true,
        disableSave: true,
        disableUndo: true,
        overrides: {
          linecolor: color,
          linewidth: 1, // Slimmer line
          linestyle: 1, // dotted line
          transparency: 70, // More transparent
          showLabel: false,
          showDateTimeRange: false,
          showPercentPriceRange: false,
        },
      },
    );

    return;
  }

  const executionShape = chart.createExecutionShape();
  executionShape
    .setText(profitText)
    .setTooltip(profitText)
    .setTextColor(color)
    .setArrowColor(color)
    .setDirection("buy") // Always "buy" to position below (arrow points up from below)
    .setTime(exitTime)
    .setArrowHeight(arrowHeight) // Conditional: 40px for longs, 20px for shorts
    .setArrowSpacing(15) // More spacing between arrow and text
    .setPrice(exitPrice)
    .setFont("13px Arial");
};

const getActionName = (actionId: number) => {
  switch (actionId) {
    case 1:
      return "Buy Spot";
    case 2:
      return "Sell Spot";
    case 3:
      return "open";
    case 4:
      return "close";
    default:
      return "?";
  }
};

const formatMovement = (movement: T.ApiMovement): Record<string, string> => {
  // Make some of the fields more human readable:
  const formattedMovement: Record<string, string> = {};

  Object.keys(movement).forEach((key) => {
    switch (key) {
      case "action_id":
        formattedMovement[key] = getActionName(movement[key]);
        break;
      case "movement_ts":
        formattedMovement.mov_ts = dayjs(movement[key]).utc(utcOff).format("YYYY-MM-DD HH:mm:ss");
        break;
      case "create_ts":
      case "exchange_order_id":
      case "fees_currency":
      case "fees_usdt":
      case "ordered_amount":
      case "expected_price":
      case "expected_slippage":
      case "slippage":
      case "spread":
      case "last_price":
      case "strategy_step_id":
      case "exchange_order_raw":
      case "meta_order":
      case "order":
      case "amount":
        formattedMovement["amount"] = movement["amount"]?.toFixed(4) || "0.00";
        break;
      case "position":
        break;
      case "price":
      case "fees":
        formattedMovement[key] = Number(movement[key]).toFixed(4);
        break;
      default:
        // eslint-disable-next-line no-case-declarations
        const fieldValue = movement[key as keyof T.ApiMovement];
        // eslint-disable-next-line no-case-declarations
        const value = String(fieldValue) === "0" ? "0.00" : String(fieldValue);
        formattedMovement[key] = value;
    }
  });

  return formattedMovement;
};

const paddedLine = (line: string, maxChars: number): string => {
  const chunks = line.match(new RegExp(`.{1,${maxChars}}`, "g")) || [];
  return chunks
    .map((chunk: string) => {
      const padding = " ·".repeat(maxChars - chunk.length);
      return `${chunk}${padding}`;
    })
    .join("\n");
};

const formatKeyValue = (key: string, value: any, maxChars = 28, minKeyLength = 7): string => {
  const paddedKey = key.padEnd(minKeyLength, "_");
  const adjustedMaxChars = maxChars - paddedKey.length - 2;

  const paddedValue = paddedLine(String(value), adjustedMaxChars);

  return `${paddedKey}: ${paddedValue}`;
};

export const createMovementMark = (
  pos: T.AppPosition,
  mov: T.ApiMovement,
  isGroupView = false,
): T.TV.Mark | T.TV.TimescaleMark => {
  // console.log('createMovementMark position, movement', pos, mov.id);
  const flatMovement = formatMovement(mov);

  const markTime = dayjs(mov.movement_ts).utc(utcOff).unix();
  // Open movement is always the first movement for this position.
  // For futures, both long and short open with BUY_FUTURE (3) and close with SELL_FUTURE (4).
  // For spot, open is BUY_SPOT (1) and close is SELL_SPOT (2).
  // The direction (long/short) is on the position, not the action.
  const isOpenMovement = [1, 3].includes(mov.action_id);

  // Tooltip is wider on TimescaleMarks, so we need to adapt the formatter
  const movementsParts: string[] = [];
  Object.entries(flatMovement).map(([key, value]) => {
    if (isOpenMovement) {
      movementsParts.push(formatKeyValue(key, value, 28));
    } else {
      movementsParts.push(`${key}: ${value}`);
    }
  });

  if (isOpenMovement) {
    // Prepend execution_id only for group views (multiple executions)
    if (isGroupView && pos.execution_id) {
      movementsParts.unshift(formatKeyValue("exec_id", pos.execution_id, 28));
    }
    const mark: T.TV.Mark = {
      id: mov.id,
      time: markTime,
      label: "O",
      labelFontColor: "#d2d2d2",
      text: movementsParts.join("\n"),
      color: {
        border: "transparent",
        background: "transparent",
      },
      minSize: 25,
    };
    return mark;
  }

  if (isPositionTrackingEnded(pos)) {
    const { type, size, leverage, collateral, fees } = calculatePositionSummary(pos);
    const positionFields = { type, size, leverage, collateral, fees };
    return {
      id: mov.id,
      time: markTime,
      label: "?",
      labelFontColor: "#d2d2d2",
      color: pos.direction_name === "long" ? LONG_COLOR : SHORT_COLOR,
      tooltip: [
        `Position: ${pos.id} - ${TRACKING_ENDED_LABEL}`,
        TRACKING_ENDED_EXPLANATION,
        "==============================",
        ...Object.entries(positionFields).map(([key, value]) => `${key}: ${value}`),
        ".",
        "Movement",
        "===================",
        ...movementsParts,
      ],
    };
  }

  const posProfit = getPositionNetProfit(pos)!;
  const getPositionSummary = (pos: T.AppPosition) => {
    // Use profit utility (with client-side fallback for execution positions)
    const profit = getPositionNetProfit(pos)!;
    const profitPct = getPositionProfitPct(pos)!;
    const posSize = pos.entry_price * pos.entry_amount;
    const totalFees = pos.total_fees_usdt || 0;

    // Calculate duration
    const duration = (() => {
      if (!pos.end_ts || !pos.start_ts) return "Unknown";
      const diff = dayjs(pos.end_ts).diff(dayjs(pos.start_ts), "minute");
      const days = Math.floor(diff / (24 * 60));
      const hours = Math.floor((diff % (24 * 60)) / 60);
      const minutes = diff % 60;

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);

      return parts.length ? parts.join(" ") : "0m";
    })();

    return {
      type: pos.direction_name,
      duration: duration,
      size: posSize.toFixed(4),
      leverage: pos.leverage || 1,
      collateral: pos.collateral_in?.toFixed(4) || 0,
      fees: totalFees.toFixed(4),
      profit: profit.toFixed(4),
      profit_pct: `${profitPct.toFixed(2)}%`,
    };
  };

  const positionFields = getPositionSummary(pos);
  const positionSummary = Object.entries(positionFields).map(([key, value]) => `${key}: ${value}`);

  const tsMark: T.TV.TimescaleMark = {
    id: mov.id,
    time: markTime,
    label: posProfit > 0 ? "W" : "L",
    labelFontColor: posProfit > 0 ? WIN_COLOR : LOSS_COLOR,
    color: pos.direction_name === "long" ? LONG_COLOR : SHORT_COLOR,
    tooltip: [
      `Position: ${pos.id} - ${posProfit > 0 ? "WIN" : "LOSS"}`,
      "==============================",
      ...positionSummary,
      ".",
      "Movement",
      "===================",
      ...(movementsParts as string[]),
    ],
  };
  return tsMark;
};

export const getTvEnumValue = (enumType: "linestyle" | "plotype", value: string): number => {
  const ENUM_MAPS: {
    linestyle: Record<string, number>;
    plotype: Record<string, number>;
  } = {
    linestyle: {
      solid: 0,
      dotted: 1,
      dashed: 2,
    },
    plotype: {
      line: 0,
      histogram: 1,
      cross: 3,
      area: 4,
      columns: 5,
      circles: 6,
      linewithbreaks: 7,
      areawithbreaks: 8,
      stepline: 9,
      steplinewithdiamonds: 10,
      steplinewithbreaks: 11,
    },
  } as const;

  const targetEnum = ENUM_MAPS[enumType];
  if (!targetEnum) throw new Error(`Unsupported enum type: ${enumType}`);

  return targetEnum[value] ?? 0;
};
